import React, { useCallback, useEffect, useState } from 'react';
import { getCloudId } from 'util/meta';
import environment from 'util/relay/environment';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { isJsmCsPEM15FastFollowsEnabled } from 'feature-flags';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { fetchQuery, graphql } from 'react-relay';
import type { State as AppState } from 'state';
import { getAccountId } from 'state/selectors/user';
import ErrorBoundary from 'view/error-boundary/error-boundary';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { TRACK_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { FieldBase } from '@atlassian/help-center-common-component/fields/field-base';
import { reportError } from '@atlassian/help-center-common-util/sentry';
import type { FieldFormProps } from '../types';
import type { entitlementPickerQuery } from './__generated__/entitlementPickerQuery.graphql';
import { EntitlementPickerDumb } from './entitlement-picker-dumb';
import { entitlementPickerLoadExperience } from './experiences';
import messages from './messages';
import type { Option } from './types';

export const ENTITLEMENTS_QUERY = graphql`
    query entitlementPickerQuery($cloudId: ID!, $accountId: ID!) {
        customerService(cloudId: $cloudId)
            @required(action: THROW)
            @optIn(to: ["CustomerServiceProductsAndEntitlementsM1"]) {
            individualByAccountId(accountId: $accountId) @required(action: THROW) {
                ... on CustomerServiceIndividual {
                    __typename
                    id @required(action: THROW)
                    entitlementList @required(action: THROW) {
                        id
                        product {
                            name
                        }
                    }
                }
            }
        }
    }
`;

export interface Props extends FieldFormProps<string> {
    reporterId?: string;
    isNewReporter?: boolean;
    enableSubmitButton: () => void;
    disableSubmitButton: () => void;
    value?: string;
}

const EntitlementPickerMain = (props: Props) => {
    di(fetchQuery, getCloudId, useAnalyticsEvents, reportError);
    const { formatMessage } = useIntl();
    const { reporterId, onChange, isNewReporter, disableSubmitButton, enableSubmitButton, value, ...fieldProps } =
        props;
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const cloudId = getCloudId();
    const [prevReporterId, setPrevReporterId] = useState<string | undefined | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [entitlements, setEntitlements] = useState<Option[]>([]);
    const [error, setError] = useState(false);
    const selectedEntitlement = entitlements.find((entitlement) => entitlement.value === value) || null;

    const updateSelectedEntitlement = useCallback(
        (option: Option | null, notFromUserInteraction?: boolean) => {
            onChange?.(props.id, option?.value || '', notFromUserInteraction);
        },
        [props.id, onChange]
    );

    const fetchEntitlements = useCallback(async () => {
        if (!reporterId) {
            return;
        }
        setIsLoading(true);
        disableSubmitButton();

        try {
            const data = await fetchQuery<entitlementPickerQuery>(environment, ENTITLEMENTS_QUERY, {
                cloudId,
                accountId: reporterId,
            }).toPromise();

            if (data?.customerService.individualByAccountId.__typename !== 'CustomerServiceIndividual') {
                throw new Error('CustomerServiceIndividual not returned');
            }

            const entitlementList = data.customerService?.individualByAccountId?.entitlementList;
            const entitlementOptions = entitlementList.map((entitlement) => ({
                label: entitlement.product.name,
                value: entitlement.id,
            }));
            setEntitlements(entitlementOptions);

            createAnalyticsEvent({
                action: 'fetched',
                analyticsType: TRACK_EVENT_TYPE,
                actionSubject: 'entitlements',
                numberOfEntitlements: entitlementOptions.length,
            }).fire();
        } catch (e) {
            setEntitlements([]);
            isJsmCsPEM15FastFollowsEnabled() && setError(true);
            reportError(e, { message: 'failed to fetch entitlements' });
            entitlementPickerLoadExperience.failure() as unknown as void;
        } finally {
            updateSelectedEntitlement(null, true);
            setIsLoading(false);
            enableSubmitButton();
        }
    }, [cloudId, createAnalyticsEvent, disableSubmitButton, enableSubmitButton, reporterId, updateSelectedEntitlement]);

    const onNewReporter = useCallback(() => {
        setPrevReporterId(reporterId);
        isJsmCsPEM15FastFollowsEnabled() && setError(false);
        entitlementPickerLoadExperience.start() as unknown as void;
    }, [reporterId]);

    const onNoEntitlements = useCallback(() => {
        setEntitlements([]);
        updateSelectedEntitlement(null, true);
        entitlementPickerLoadExperience.success() as unknown as void;
    }, [updateSelectedEntitlement]);

    useEffect(() => {
        if (reporterId === prevReporterId) {
            return;
        }
        onNewReporter();
        // reporterId is '' if the reporter field has been cleared
        // reporterID is undefined if the user is anonymous
        if (isNewReporter || reporterId === '' || reporterId === undefined) {
            onNoEntitlements();
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchEntitlements();
    }, [
        cloudId,
        fetchEntitlements,
        isNewReporter,
        onNewReporter,
        onNoEntitlements,
        prevReporterId,
        reporterId,
        updateSelectedEntitlement,
    ]);

    useEffect(() => {
        createAnalyticsEvent({
            action: 'viewed',
            analyticsType: TRACK_EVENT_TYPE,
            actionSubject: 'entitlementPicker',
        }).fire();
    }, [createAnalyticsEvent]);

    if (error && isJsmCsPEM15FastFollowsEnabled()) {
        return (
            <FieldBase label={fieldProps.label} htmlFor="" required={fieldProps.required}>
                <Box xcss={ErrorMessageStyles}>
                    <ErrorIcon label="" primaryColor={token('color.icon.danger', '#c9372c')} />
                    {formatMessage(messages.errorMessage)}
                </Box>
            </FieldBase>
        );
    }

    return (
        <EntitlementPickerDumb
            {...fieldProps}
            onChange={updateSelectedEntitlement}
            isLoading={isLoading}
            selectedEntitlement={selectedEntitlement}
            entitlements={entitlements}
        />
    );
};

const EntitlementPickerWithErrorBoundary = (props: Props) => (
    <ErrorBoundary id="entitlement-picker" packageName="portal-fields">
        <EntitlementPickerMain {...props} />
    </ErrorBoundary>
);

const selector = createSelector(
    // if there is a reporter field in the form we use that value, otherwise we use the current user's account id
    (state: AppState, props: Props) => (props.reporterId === undefined ? getAccountId(state) : props.reporterId),
    (reporterId) => ({ reporterId })
);

export const EntitlementPicker = connect(selector)(EntitlementPickerWithErrorBoundary);

const ErrorMessageStyles = xcss({
    display: 'flex',
    alignItems: 'center',
    lineHeight: '20px',
    gap: 'space.050',
    marginTop: token('space.100', '8px'),
});
